/*
 * halal-label/logo-process.js — the Label Halal file a business owner
 * downloaded from SIHALAL, made printable in the colour the rules allow.
 *
 * Where the file comes from: BPJPH FAQ "Label Halal" no. 10 — "Di akun SIHALAL
 * masing-masing". So the page never ships the logo itself (it is not handed
 * out as a picture here, and nothing is drawn without the person's own file
 * and certificate number). What the page does with that file:
 *   - finds the ink against the background (a transparent PNG or a flat
 *     background colour sampled from the border);
 *   - crops to the ink;
 *   - keeps the file's own ungu, or repaints it black or white — the only
 *     three colours the label may use (FAQ no. 7-8: "Hanya ungu, hitam, dan
 *     putih"), and says when the file's ink is none of them;
 *   - notices a GREEN logo: the old MUI logo is green and has been unlawful on
 *     packaging since 2 Februari 2026 (FAQ no. 11).
 *
 * Pure: works on an RGBA array, so node --test can feed it synthetic images.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalLogo = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    // 'asli' = the file's own colour (its ungu); black and white are exact.
    var PALETTE = { asli: null, hitam: '#000000', putih: '#ffffff' };

    function median(arr) {
        var a = arr.slice().sort(function (x, y) { return x - y; });
        return a.length ? a[a.length >> 1] : 0;
    }

    function rgbToHsl(r, g, b) {
        r /= 255; g /= 255; b /= 255;
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        var h = 0, s = 0, l = (mx + mn) / 2;
        if (mx !== mn) {
            var d = mx - mn;
            s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
            if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
            else if (mx === g) h = (b - r) / d + 2;
            else h = (r - g) / d + 4;
            h *= 60;
        }
        return { h: h, s: s, l: l };
    }

    /* rgba: Uint8ClampedArray (w*h*4). Returns
       { mask: Uint8ClampedArray(w*h), bbox:{x,y,w,h}|null, ink:{r,g,b,hsl},
         transparentBg, coverage, greenish, purplish } */
    function analyze(rgba, w, h) {
        var n = w * h;
        // Border sample: is the background transparent, or a flat colour?
        var br = [], bg = [], bb = [], ba = [];
        function sample(x, y) {
            var i = (y * w + x) * 4;
            br.push(rgba[i]); bg.push(rgba[i + 1]); bb.push(rgba[i + 2]); ba.push(rgba[i + 3]);
        }
        var stepX = Math.max(1, Math.floor(w / 64)), stepY = Math.max(1, Math.floor(h / 64));
        for (var x = 0; x < w; x += stepX) { sample(x, 0); sample(x, h - 1); }
        for (var y = 0; y < h; y += stepY) { sample(0, y); sample(w - 1, y); }
        var transparentBg = median(ba) < 128;
        var bgR = median(br), bgG = median(bg), bgB = median(bb);

        var mask = new Uint8ClampedArray(n);
        var minX = w, minY = h, maxX = -1, maxY = -1, cnt = 0;
        var sr = 0, sg = 0, sb = 0, sn = 0;
        for (var p = 0; p < n; p++) {
            var i4 = p * 4;
            var a = rgba[i4 + 3];
            var v;
            if (transparentBg) {
                v = a;
            } else {
                var d = Math.max(Math.abs(rgba[i4] - bgR), Math.abs(rgba[i4 + 1] - bgG), Math.abs(rgba[i4 + 2] - bgB));
                // anti-aliased edges ramp from 24 to 120 of channel distance
                v = d <= 24 ? 0 : (d >= 120 ? 255 : Math.round((d - 24) / 96 * 255));
                v = Math.round(v * a / 255);
            }
            mask[p] = v;
            if (v > 32) {
                var px = p % w, py = (p / w) | 0;
                if (px < minX) minX = px; if (px > maxX) maxX = px;
                if (py < minY) minY = py; if (py > maxY) maxY = py;
                cnt++;
            }
            if (v > 200) { sr += rgba[i4]; sg += rgba[i4 + 1]; sb += rgba[i4 + 2]; sn++; }
        }
        var out = { mask: mask, transparentBg: transparentBg, coverage: cnt / n, bbox: null, ink: null };
        if (maxX < 0) return out;
        out.bbox = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
        if (sn) {
            var r = sr / sn, g = sg / sn, b = sb / sn;
            var hsl = rgbToHsl(r, g, b);
            out.ink = { r: Math.round(r), g: Math.round(g), b: Math.round(b), hsl: hsl };
            // the old MUI logo: saturated green
            out.greenish = hsl.s > 0.25 && hsl.l > 0.12 && hsl.l < 0.85 && hsl.h >= 80 && hsl.h <= 170;
            out.purplish = hsl.s > 0.2 && hsl.h >= 270 && hsl.h <= 330;
        }
        return out;
    }

    /* Repaint: the cropped mask as an RGBA image (transparent around), plus
       `pad` pixels of margin. hex === null keeps the file's own colour —
       the DEFAULT: the official ungu is whatever SIHALAL put in the file, and
       re-tinting it with a hex we read in a news report would move it. Black
       and white are exact by definition. */
    function recolor(an, w, hex, pad, rgba) {
        pad = pad || 0;
        var bb = an.bbox;
        var ow = bb.w + 2 * pad, oh = bb.h + 2 * pad;
        var out = new Uint8ClampedArray(ow * oh * 4);
        var keep = hex == null;
        var c = keep ? 0 : parseInt(String(hex).replace('#', ''), 16);
        var R = (c >> 16) & 255, G = (c >> 8) & 255, B = c & 255;
        for (var y = 0; y < bb.h; y++) {
            for (var x = 0; x < bb.w; x++) {
                var p = (bb.y + y) * w + (bb.x + x);
                var m = an.mask[p];
                if (!m) continue;
                var o = ((y + pad) * ow + (x + pad)) * 4;
                if (keep && rgba) {
                    // un-mix the anti-aliased edge from the background: use the
                    // ink colour measured on solid pixels, alpha from the mask
                    var ink = an.ink || { r: rgba[p * 4], g: rgba[p * 4 + 1], b: rgba[p * 4 + 2] };
                    out[o] = ink.r; out[o + 1] = ink.g; out[o + 2] = ink.b;
                } else { out[o] = R; out[o + 1] = G; out[o + 2] = B; }
                out[o + 3] = m;
            }
        }
        return { data: out, w: ow, h: oh };
    }

    /* Is the file's own ink one of the three allowed colours? Purple is judged
       by hue (we do not trust a hex we could not read in the act itself);
       black and white by lightness. */
    function inkVerdict(an) {
        if (!an || !an.ink) return 'unknown';
        var hsl = an.ink.hsl;
        if (an.greenish) return 'green';
        if (hsl.s < 0.18 && hsl.l < 0.25) return 'black';
        if (hsl.s < 0.18 && hsl.l > 0.85) return 'white';
        if (an.purplish) return 'purple';
        return 'other';
    }

    return { PALETTE: PALETTE, analyze: analyze, recolor: recolor, inkVerdict: inkVerdict, rgbToHsl: rgbToHsl };
});
